// src/features/itinerary/utils/itineraryUtils.js
// -------------------------------------------------------------
// Itinerary utilities
// - Group segments by local "day".
// - Sort chronologically (timezone-aware).
// - Insert layovers/stopovers.
// - Apply rules so impossible segments (hotel check-in during
//   layover, etc.) are re-ordered.
// -------------------------------------------------------------

import {
  parseIsoDate, // still used for legacy / timezone-agnostic fields (e.g. tracks)
  getLocalEndpointInfo,
  parseLocalDateTime,
} from "../../../utils/dateUtils";

// import { debugTimelineForDay } from "./itineraryDebug";

/**
 * Format a YYYY-MM-DD day key as a friendly heading.
 * Example: "2025-06-07" -> "Saturday, June 07, 2025"
 */
export function formatDayHeading(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "2-digit",
  });
}

/**
 * Group raw segments into day buckets with:
 *   - timezone-aware local date grouping
 *   - chronological sorting within each day
 *   - layovers/stopovers + rule-based reordering
 *
 * Returns:
 *   [
 *     { day: "YYYY-MM-DD", segments: [seg, seg, ...] },
 *     ...
 *   ]
 */
export function groupByDay(items) {
  // 1) Expand each item into one or two "day instances"
  const instances = explodeIntoDayInstances(items);

  // 2) Bucket by day
  const buckets = new Map();
  for (const inst of instances) {
    const dayKey = inst._day; // already a local YYYY-MM-DD string
    if (!dayKey) continue;
    if (!buckets.has(dayKey)) buckets.set(dayKey, []);
    buckets.get(dayKey).push(inst);
  }

  // 3) Sort within day (local time), then insert layovers & apply rules
  const days = Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, segs]) => {
      const sorted = [...segs].sort((a, b) => {
        const ak = timeKeyForSort(a);
        const bk = timeKeyForSort(b);

        // Keep these visible for debugging if needed
        a.sortTimeKey = ak;
        b.sortTimeKey = bk;

        if (ak !== bk) return ak - bk;

        // Tie-breakers to keep things stable and sensible:
        // 1) start before end
        const aRank = instanceRank(a);
        const bRank = instanceRank(b);
        if (aRank !== bRank) return aRank - bRank;

        // 2) layovers snug between flights (already inserted post-sort, but keep stable)
        const aLay = isLayover(a) ? 1 : 0;
        const bLay = isLayover(b) ? 1 : 0;
        if (aLay !== bLay) return aLay - bLay;

        // 3) stable final fallback
        return String(a.id || "").localeCompare(String(b.id || ""));
      });

      // ✅ DEBUG 1 – chronological ordering for the day
      // debugTimelineForDay("sorted-chronological", day, sorted);

      const withLayoversAndRules = insertLayovers(sorted);

      // ✅ DEBUG 2 – after layovers + rules (deferred segments, etc.)
      // debugTimelineForDay("after-rules", day, withLayoversAndRules);

      return { day, segments: withLayoversAndRules };
    });

  return days;
}

/**
 * Map each segment to a color + icon CSS class pair for the "Path" column.
 */
export function getPathIconClasses(item) {
  let bgClass = "neutral";
  let maskClass = "svg-icon-mask-clock";
  let segmentType = item.type ?? "";
  let segmentSubType = "";

  if (item.type === "transportation") segmentSubType = item?.mode || "";
  if (item.type === "accommodation") segmentSubType = item?.spaceType || "";
  if (item.type === "activity") segmentSubType = item?.activityType || "";

  if (isLayover(item)) {
    return {
      bgClass: "neutral",
      maskClass: "svg-icon-mask-clock",
      segmentType,
      segmentSubType,
    };
  }

  if (item.type === "tracks") return { isTracks: true };

  if (item.type === "transportation") {
    switch ((item.mode || "").toLowerCase()) {
      case "airplane":
        bgClass = "blue";
        maskClass = "svg-icon-mask-flight";
        break;
      case "car":
        bgClass = "blue";
        maskClass = "svg-icon-mask-car";
        break;
      case "train":
      case "rail":
        bgClass = "blue";
        maskClass = "svg-icon-mask-train";
        break;
      case "boat":
        bgClass = "blue";
        maskClass = "svg-icon-mask-boat";
        break;
      default:
        bgClass = "blue";
        maskClass = "svg-icon-mask-directions";
    }
  } else if (item.type === "accommodation") {
    bgClass = "brown";
    maskClass = "svg-icon-mask-bed";
  } else if (item.type === "idea") {
    bgClass = "yellow";
    maskClass = "svg-icon-mask-idea";
  } else if (item.type === "activity") {
    switch ((item.activityType || "").toLowerCase()) {
      case "walk":
      case "hike":
        bgClass = "green";
        maskClass = "svg-icon-mask-walk";
        break;
      case "tour":
        bgClass = "green";
        maskClass = "svg-icon-mask-tour";
        break;
      case "dining":
        bgClass = "brown";
        maskClass = "svg-icon-mask-dining";
        break;
      default:
        bgClass = "green";
        maskClass = "svg-icon-mask-idea";
    }
  }

  return { bgClass, maskClass, segmentType, segmentSubType };
}

/**
 * Human-readable title for a segment row.
 */
export function getSegmentTitle(item) {
  if (isLayover(item)) {
    return item.title || "Layover";
  }

  if (item.type === "transportation" && item.mode === "airplane") {
    const flight =
      item.providerNumber || item.providerCode || item.providerName || "";
    if (item.originCode && item.destinationCode) {
      return `${item.originCode} - ${item.destinationCode}${
        flight ? ` (${flight})` : ""
      }`;
    }
    return item.providerName || "Transportation";
  }

  if (
    item.type === "transportation" &&
    item.mode === "car" &&
    (item.terms || "").toLowerCase() === "own"
  ) {
    return (
      <>
        Road Trip!!! <br />( {item.originName} to {item.destinationName} )
      </>
    );
  }

  if (item.type === "accommodation") {
    return item.providerName || "Accommodation";
  }

  if (item.type === "activity" && item.activityType === "idea") {
    return item.activityName ? `How about... ${item.activityName}` : "Activity";
  }

  if (item.type === "activity") {
    return item.activityName || "Activity";
  }

  if (item.terms === "rent") {
    return item.providerName || item.title || "Rental";
  }

  if (item.type === "tracks") {
    return item.title || "Track";
  }

  return item.title || item.name || item.providerName || "Activity";
}

/**
 * Compute one or two "time blocks" for display in the Time column:
 *   [{ icon: string|null, time: "HH:MM", tz: "PST" }, ...]
 *
 * This is now timezone-aware. We always use the *local* timezone
 * that corresponds to the specific endpoint (departure/arrival,
 * pickup/return, check-in/check-out, start/end).
 */
export function computeTimeBlocks(item) {
  const part = item._instance || "full";

  // Layover: show as duration only
  if (isLayover(item)) {
    return [{ icon: null, time: item.durationText || "", tz: "" }];
  }

  // TRANSPORTATION
  if (isTransportation(item)) {
    const terms = (item.terms || "").toLowerCase();

    // 1) Hired transportation (flight, bus, train, boat, etc.)
    if (terms !== "rent") {
      const depart = getLocalEndpointInfo(
        item.departureDateTime,
        item.departureTimezone || item.timezone
      );
      const arrive = getLocalEndpointInfo(
        item.arrivalDateTime,
        item.arrivalTimezone || item.timezone
      );

      const startIcon =
        item.mode === "airplane" ? "planeDepart" : "transportDepart";
      const endIcon =
        item.mode === "airplane" ? "planeArrive" : "transportArrive";

      if (part === "start") {
        return [
          {
            icon: startIcon,
            time: visibleTime(depart?.time),
            tz: depart?.tzAbbr || "",
          },
        ];
      }
      if (part === "end") {
        return [
          {
            icon: endIcon,
            time: visibleTime(arrive?.time),
            tz: arrive?.tzAbbr || "",
          },
        ];
      }

      return [
        {
          icon: startIcon,
          time: visibleTime(depart?.time),
          tz: depart?.tzAbbr || "",
        },
        {
          icon: endIcon,
          time: visibleTime(arrive?.time),
          tz: arrive?.tzAbbr || "",
        },
      ];
    }

    // 2) Rental transportation (e.g. rental car)
    const pickup = getLocalEndpointInfo(
      item.pickupDateTime,
      item.pickupTimezone || item.timezone
    );
    const ret = getLocalEndpointInfo(
      item.returnDateTime,
      item.returnTimezone || item.timezone
    );

    const startIcon = "carRentalPickup";
    const endIcon = "carRentalReturn";

    if (part === "start") {
      return [
        {
          icon: startIcon,
          time: visibleTime(pickup?.time),
          tz: pickup?.tzAbbr || "",
        },
      ];
    }
    if (part === "end") {
      return [
        {
          icon: endIcon,
          time: visibleTime(ret?.time),
          tz: ret?.tzAbbr || "",
        },
      ];
    }

    return [
      {
        icon: startIcon,
        time: visibleTime(pickup?.time),
        tz: pickup?.tzAbbr || "",
      },
      {
        icon: endIcon,
        time: visibleTime(ret?.time),
        tz: ret?.tzAbbr || "",
      },
    ];
  }

  // ACCOMMODATION
  if (isAccommodation(item)) {
    const checkIn = getLocalEndpointInfo(item.checkInDateTime, item.timezone);
    const checkOut = getLocalEndpointInfo(item.checkOutDateTime, item.timezone);

    if (part === "start") {
      return [
        {
          icon: "checkin",
          time: visibleTime(checkIn?.time),
          tz: checkIn?.tzAbbr || "",
        },
      ];
    }
    if (part === "end") {
      return [
        {
          icon: "checkout",
          time: visibleTime(checkOut?.time),
          tz: checkOut?.tzAbbr || "",
        },
      ];
    }

    return [
      {
        icon: "checkin",
        time: visibleTime(checkIn?.time),
        tz: checkIn?.tzAbbr || "",
      },
      {
        icon: "checkout",
        time: visibleTime(checkOut?.time),
        tz: checkOut?.tzAbbr || "",
      },
    ];
  }

  // ACTIVITIES / IDEAS
  if (isActivity(item) || item.type === "idea") {
    const start = getLocalEndpointInfo(
      item.startDateTime,
      item.startTimezone || item.timezone
    );
    const end = getLocalEndpointInfo(
      item.endDateTime,
      item.endTimezone || item.timezone
    );

    if (part === "start") {
      return [
        {
          icon: "start",
          time: visibleTime(start?.time),
          tz: start?.tzAbbr || "",
        },
      ];
    }
    if (part === "end") {
      return [
        {
          icon: "end",
          time: visibleTime(end?.time),
          tz: end?.tzAbbr || "",
        },
      ];
    }

    const startTime = visibleTime(start?.time);
    const endTime = visibleTime(end?.time);

    if (startTime && endTime) {
      return [
        { icon: "start", time: startTime, tz: start?.tzAbbr || "" },
        { icon: "end", time: endTime, tz: end?.tzAbbr || "" },
      ];
    }
    if (startTime)
      return [{ icon: "start", time: startTime, tz: start?.tzAbbr || "" }];
    if (endTime) return [{ icon: "end", time: endTime, tz: end?.tzAbbr || "" }];

    return [{ icon: null, time: "", tz: "" }];
  }

  // TRACKS
  if (item.type === "tracks") {
    const d = new Date(item.created);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return [{ icon: null, time: `${hh}:${mm}`, tz: "" }];
  }

  // Fallback
  return [{ icon: null, time: "", tz: "" }];
}

/**
 * Build a flat list of contact items for rendering in ContactsRow.
 */
export function buildContacts(item = {}) {
  const c = item.contacts || {};
  const out = [];
  if (c.address) out.push({ kind: "address", value: c.address, icon: "mail" });
  if (c.phone) out.push({ kind: "phone", value: c.phone, icon: "phone" });
  if (c.email) out.push({ kind: "email", value: c.email, icon: "email" });
  if (c.web) out.push({ kind: "web", value: c.web, icon: "web" });
  if (c.map !== undefined)
    out.push({ kind: "map", value: c.map || "", icon: "images/map.svg" });
  return out;
}

/**
 * Build link items for the "Links" column.
 */
export function buildLinks(item = {}) {
  const links = item.links || {};
  const out = [];

  const v = "placeholder";
  if (item?.coordinates?.longitude && item?.coordinates?.latitude) {
    out.push({ kind: "map", v, icon: "map" });
  }

  Object.entries(links).forEach(([key, value]) => {
    if (!value) return;
    const iconFile =
      key === "status"
        ? "status"
        : key === "reservation"
        ? "reservation"
        : "web";
    out.push({ kind: key, value, icon: iconFile });
  });

  return out;
}

// -------------------------------------------------------------
// Internal helpers: time & timezone handling
// -------------------------------------------------------------

// /**
//  * Convert a UTC ISO string + IANA timezone into:
//  *   {
//  *     date: Date (local wall-clock, stored as UTC internally),
//  *     dayKey: "YYYY-MM-DD",
//  *     time: "HH:MM",
//  *     minutesSinceMidnight: number,
//  *     tzAbbr: "PST" | "GMT-6" | etc.
//  *   }
//  *
//  * If ianaTimezone is missing, falls back to parseIsoDate (system timezone).
//  */
// function getLocalEndpointInfo(isoUtc, ianaTimezone) {
//   if (!isoUtc) return null;
//   try {
//     const utc = new Date(isoUtc);
//     if (Number.isNaN(utc.getTime())) return null;

//     if (!ianaTimezone) {
//       // Fallback: use parseIsoDate (system zone)
//       const p = parseIsoDate(isoUtc);
//       const minutesSinceMidnight =
//         Number(p.hour || 0) * 60 + Number(p.minutes || 0);
//       return {
//         date: utc,
//         dayKey: p.yyyyMmDd,
//         time: p.shortTime,
//         minutesSinceMidnight,
//         tzAbbr: p.timeZoneAbbreviation || "UTC",
//       };
//     }

//     // Use Intl.DateTimeFormat to get local parts in the given IANA zone
//     const fmt = new Intl.DateTimeFormat("en-US", {
//       timeZone: ianaTimezone,
//       year: "numeric",
//       month: "2-digit",
//       day: "2-digit",
//       hour: "2-digit",
//       minute: "2-digit",
//       second: "2-digit",
//       hour12: false,
//       timeZoneName: "short",
//     });

//     const parts = fmt.formatToParts(utc);
//     const get = (type) => parts.find((p) => p.type === type)?.value || "";
//     const year = Number(get("year"));
//     const month = Number(get("month"));
//     const day = Number(get("day"));
//     const hour = Number(get("hour"));
//     const minute = Number(get("minute"));
//     const second = Number(get("second"));
//     const tzAbbr = get("timeZoneName") || "UTC";

//     const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
//     const dayKey = `${year}-${String(month).padStart(2, "0")}-${String(
//       day
//     ).padStart(2, "0")}`;
//     const time = `${String(hour).padStart(2, "0")}:${String(minute).padStart(
//       2,
//       "0"
//     )}`;
//     const minutesSinceMidnight = hour * 60 + minute;

//     return { date, dayKey, time, minutesSinceMidnight, tzAbbr };
//   } catch {
//     return null;
//   }
// }

/**
 * For layover/stopover logic we need a full "span" in local time:
 *   { startMs, endMs }
 * Uses the appropriate fields and timezones based on segment type.
 */
function getSegmentLocalSpan(item) {
  // Transportation (hire)
  if (isTransportation(item) && (item.terms || "").toLowerCase() !== "rent") {
    const dep = getLocalEndpointInfo(
      item.departureDateTime,
      item.departureTimezone || item.timezone
    );
    const arr = getLocalEndpointInfo(
      item.arrivalDateTime,
      item.arrivalTimezone || item.timezone
    );
    return {
      startMs: dep?.date?.getTime() ?? null,
      endMs: arr?.date?.getTime() ?? null,
    };
  }

  // Transportation (rental)
  if (isTransportation(item) && (item.terms || "").toLowerCase() === "rent") {
    const pick = getLocalEndpointInfo(
      item.pickupDateTime,
      item.pickupTimezone || item.timezone
    );
    const ret = getLocalEndpointInfo(
      item.returnDateTime,
      item.returnTimezone || item.timezone
    );
    return {
      startMs: pick?.date?.getTime() ?? null,
      endMs: ret?.date?.getTime() ?? null,
    };
  }

  // Accommodation
  if (isAccommodation(item)) {
    const checkIn = getLocalEndpointInfo(item.checkInDateTime, item.timezone);
    const checkOut = getLocalEndpointInfo(item.checkOutDateTime, item.timezone);
    return {
      startMs: checkIn?.date?.getTime() ?? null,
      endMs: checkOut?.date?.getTime() ?? null,
    };
  }

  // Activity / Idea
  if (isActivity(item) || item.type === "idea") {
    const start = getLocalEndpointInfo(
      item.startDateTime,
      item.startTimezone || item.timezone
    );
    const end = getLocalEndpointInfo(
      item.endDateTime,
      item.endTimezone || item.timezone
    );
    return {
      startMs: start?.date?.getTime() ?? null,
      endMs: end?.date?.getTime() ?? null,
    };
  }

  // Tracks or unknown
  return { startMs: null, endMs: null };
}

// -------------------------------------------------------------
// Internal helpers: segment classification
// -------------------------------------------------------------

function isTransportation(item) {
  return item && item.type === "transportation" && item.mode !== "layover";
}

function isAccommodation(item) {
  return item && item.type === "accommodation";
}

function isActivity(item) {
  return item && item.type === "activity";
}

/**
 * Identify layover/stopover segments.
 *
 * NOTE: CHANGED from original implementation:
 * - Previously: type === "note" && mode === "layover"
 * - Now:        type === "transportation" && mode === "layover"
 */
function isLayover(x) {
  return x && x.type === "transportation" && x.mode === "layover";
}

// -------------------------------------------------------------
// Internal helpers: explode items into "day instances"
// -------------------------------------------------------------

/**
 * Expand each logical segment into one or two "day instances":
 *   - For segments that begin and end on the same local day -> one instance:
 *       { ...item, _instance: "full", _day: "YYYY-MM-DD" }
 *   - For segments that span days -> two instances:
 *       { ...item, _instance: "start", _day: startDay, id: "id_start" }
 *       { ...item, _instance: "end",   _day: endDay,   id: "id_end"   }
 */
function explodeIntoDayInstances(items = []) {
  const out = [];

  for (const item of items) {
    // Tracks are single-day "point in time"
    if (item.type === "tracks") {
      const d = toYMD(new Date(item.created));
      out.push({ ...item, _instance: "full", _day: d, id: ensureId(item.id) });
      continue;
    }

    // Determine a normalized local span with day keys
    const span = normalizeSpan(item);
    if (!span.startDate) continue;

    if (!span.endDate || span.startDate === span.endDate) {
      out.push({
        ...item,
        _instance: "full",
        _day: span.startDate,
        id: ensureId(item.id),
      });
    } else {
      const startInst = {
        ...item,
        _instance: "start",
        _day: span.startDate,
        id: `${ensureId(item.id)}_start`,
      };
      const endInst = {
        ...item,
        _instance: "end",
        _day: span.endDate,
        id: `${ensureId(item.id)}_end`,
      };
      out.push(startInst, endInst);
    }
  }

  return out;
}

/**
 * Normalize each segment into a local start/end "span":
 *   { startDate: "YYYY-MM-DD", startTime: "HH:MM", endDate, endTime }
 *
 * Uses the appropriate UTC field + IANA timezone field based on type.
 */
function normalizeSpan(item) {
  // TRANSPORTATION (hire)
  if (isTransportation(item) && (item.terms || "").toLowerCase() !== "rent") {
    const dep = getLocalEndpointInfo(
      item.departureDateTime,
      item.departureTimezone || item.timezone
    );
    const arr = getLocalEndpointInfo(
      item.arrivalDateTime,
      item.arrivalTimezone || item.timezone
    );
    return {
      startDate: dep?.dayKey || null,
      startTime: dep?.time || null,
      endDate: arr?.dayKey || null,
      endTime: arr?.time || null,
    };
  }

  // TRANSPORTATION (rental)
  if (isTransportation(item) && (item.terms || "").toLowerCase() === "rent") {
    const pick = getLocalEndpointInfo(
      item.pickupDateTime,
      item.pickupTimezone || item.timezone
    );
    const ret = getLocalEndpointInfo(
      item.returnDateTime,
      item.returnTimezone || item.timezone
    );
    return {
      startDate: pick?.dayKey || null,
      startTime: pick?.time || null,
      endDate: ret?.dayKey || null,
      endTime: ret?.time || null,
    };
  }

  // ACCOMMODATION
  if (isAccommodation(item)) {
    const checkIn = getLocalEndpointInfo(item.checkInDateTime, item.timezone);
    const checkOut = getLocalEndpointInfo(item.checkOutDateTime, item.timezone);
    return {
      startDate: checkIn?.dayKey || null,
      startTime: checkIn?.time || null,
      endDate: checkOut?.dayKey || null,
      endTime: checkOut?.time || null,
    };
  }

  // ACTIVITY / IDEA
  if (isActivity(item) || item.type === "idea") {
    const start = getLocalEndpointInfo(
      item.startDateTime,
      item.startTimezone || item.timezone
    );
    const end = getLocalEndpointInfo(
      item.endDateTime,
      item.endTimezone || item.timezone
    );
    return {
      startDate: start?.dayKey || end?.dayKey || null,
      startTime: start?.time || null,
      endDate: end?.dayKey || start?.dayKey || null,
      endTime: end?.time || null,
    };
  }

  // Fallback: try generic start/end fields with parseIsoDate
  const tryStart =
    item.startDateTime ||
    item.dateTime ||
    item.departureDateTime ||
    item.checkInDateTime ||
    null;
  const tryEnd =
    item.endDateTime || item.arrivalDateTime || item.checkOutDateTime || null;

  let startDate = null;
  let startTime = null;
  let endDate = null;
  let endTime = null;

  if (tryStart) {
    const p = parseIsoDate(tryStart);
    startDate = p.yyyyMmDd;
    startTime = p.shortTime;
  }
  if (tryEnd) {
    const p = parseIsoDate(tryEnd);
    endDate = p.yyyyMmDd;
    endTime = p.shortTime;
  }

  return { startDate, startTime, endDate, endTime };
}

// -------------------------------------------------------------
// Internal helpers: sorting
// -------------------------------------------------------------

/**
 * Produce a numeric key for sorting *within a given day bucket*.
 * We use local "minutes since midnight" where possible.
 */
function timeKeyForSort(item) {
  const part = item._instance || "full";

  // Layovers: roughly middle-of-day so they stay between flights
  if (isLayover(item)) {
    return item._sortMinutes ?? 720;
  }

  // Tracks: use created timestamp
  if (item.type === "tracks") {
    try {
      const d = new Date(item.created);
      return d.getHours() * 60 + d.getMinutes();
    } catch {
      return 720;
    }
  }

  // General case: rely on getSegmentLocalSpan
  const span = getSegmentLocalSpan(item);
  const startMs = span.startMs;
  const endMs = span.endMs;

  if (part === "start") {
    return msToMinutes(startMs, 0);
  }
  if (part === "end") {
    return msToMinutes(endMs, 1439);
  }

  // full: prefer start, fallback to end, then middle-of-day
  if (startMs != null) return msToMinutes(startMs, 0);
  if (endMs != null) return msToMinutes(endMs, 1439);
  return 720;
}

function msToMinutes(ms, fallback) {
  if (ms == null) return fallback;
  const d = new Date(ms);
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

/**
 * Rank for tie-breaking:
 *   start < full < end
 */
function instanceRank(item) {
  const p = item._instance || "full";
  return p === "start" ? 0 : p === "full" ? 1 : 2;
}

// -------------------------------------------------------------
// Internal helpers: layovers & reordering rules
// -------------------------------------------------------------

/**
 * Insert layovers/stopovers *and* apply rule-based reordering:
 *
 * Strategy (per day bucket):
 *   1. We already have segments sorted chronologically (local time).
 *   2. We scan for pairs of transportation segments that:
 *      - share an endpoint (same airport)
 *      - have an arrival then departure with a gap
 *      - 30–1440 minutes -> Layover
 *      - >1440 minutes   -> Stopover (we currently just treat as layover with different label)
 *   3. After we identify a layover window [startMs, endMs],
 *      we look at segments whose local times fall inside that window:
 *
 *      - Activity segments are allowed to stay between the flights.
 *      - Accommodation / rental / other "rule" segments:
 *          * If start is inside and end is outside -> move AFTER second transport.
 *          * If end is inside and start is outside -> move BEFORE first transport.
 *          * If both inside -> leave in place (exception case).
 */
function insertLayovers(daySegments) {
  if (!Array.isArray(daySegments) || daySegments.length < 2) return daySegments;

  const n = daySegments.length;

  /**
   * "Core" transport legs that can form layover pairs.
   * - We exclude layovers themselves (mode === "layover").
   * - We exclude rentals (terms === "rent") because they are not airport-to-airport legs.
   *   Rentals (car hire) are treated as "ground segments" that may need to be deferred,
   *   not as the segments that define a layover.
   */
  function isCoreTransport(seg) {
    if (!seg || seg.type !== "transportation") return false;
    if (seg.mode === "layover") return false;
    if ((seg.terms || "").toLowerCase() === "rent") return false;
    return true;
  }

  /**
   * For non-core-transport segments, we need a single "representative" time in ms
   * that we can compare against the travel corridor windows.
   *
   * - For most segments we use the *start* of the local span (if available).
   * - For accommodations we now use the *actual check-in time*, not end-of-day.
   * - For rentals (transportation with terms=rent) we use the pickup time.
   */
  function segmentTimeMs(seg) {
    // 1) Accommodation: use local check-in time
    if (seg.type === "accommodation" && seg.checkInDateTime) {
      try {
        const parts = parseIsoDate(seg.checkInDateTime);
        const d = parseLocalDateTime(parts.yyyyMmDd, parts.shortTime);
        return d.getTime();
      } catch {
        // fall through to generic path
      }
    }

    // 2) Transportation rentals (car rental, etc): use pickup time
    if (
      seg.type === "transportation" &&
      (seg.terms || "").toLowerCase() === "rent" &&
      seg.pickupDateTime
    ) {
      try {
        const parts = parseIsoDate(seg.pickupDateTime);
        const d = parseLocalDateTime(parts.yyyyMmDd, parts.shortTime);
        return d.getTime();
      } catch {
        // fall through to generic path
      }
    }

    // 3) Generic fallback: use timeKeyForSort (minutes from midnight) on seg._day
    const k = timeKeyForSort(seg);
    if (typeof k === "number" && seg._day) {
      const base = parseLocalDateTime(seg._day, "00:00");
      return base.getTime() + k * 60 * 1000;
    }
    if (typeof k === "number") {
      return k * 60 * 1000;
    }

    return 0;
  }

  // Collect indexes of core transportation segments for this day
  const transportIndexes = [];
  for (let i = 0; i < n; i++) {
    if (isCoreTransport(daySegments[i])) {
      transportIndexes.push(i);
    }
  }

  if (transportIndexes.length < 2) {
    // Nothing to pair, just return as-is
    return daySegments;
  }

  /**
   * Each entry in layoverPairs describes:
   * - the two core transport rows (indices i, j)
   * - the layover duration (arrival1 → departure2)
   * - the "travel corridor" window (departure1 → arrival2)
   * - a synthetic layover row to insert
   * - a list of non-activity segments that must be deferred
   */
  const layoverPairs = [];

  for (let t = 0; t < transportIndexes.length - 1; t++) {
    const i = transportIndexes[t];
    const j = transportIndexes[t + 1];

    const curr = daySegments[i];
    const next = daySegments[j];

    // We only care about legs that have both arrival and departure sides present.
    const parsedDep1 = curr.departureDateTime
      ? parseIsoDate(curr.departureDateTime)
      : null;
    const parsedArr1 = curr.arrivalDateTime
      ? parseIsoDate(curr.arrivalDateTime)
      : null;
    const parsedDep2 = next.departureDateTime
      ? parseIsoDate(next.departureDateTime)
      : null;
    const parsedArr2 = next.arrivalDateTime
      ? parseIsoDate(next.arrivalDateTime)
      : null;

    const currHasArrival =
      !!(parsedArr1 && parsedArr1.yyyyMmDd && parsedArr1.shortTime) &&
      curr._instance !== "start";
    const nextHasDepart =
      !!(parsedDep2 && parsedDep2.yyyyMmDd && parsedDep2.shortTime) &&
      next._instance !== "end";

    if (!currHasArrival || !nextHasDepart) {
      continue;
    }

    // Must be same airport/station for a layover
    const sameAirport =
      (curr.destinationCode &&
        next.originCode &&
        curr.destinationCode === next.originCode) ||
      (!!curr.destinationName &&
        !!next.originName &&
        normalize(curr.destinationName) === normalize(next.originName));

    if (!sameAirport) {
      continue;
    }

    // [ ] a better solution is needed to order (driving -> ferry -> hotel) so that hotel checkin does not order between driving and ferry. One option, allow user or automate final destination.
    // New rule (already in your version): layovers only occur between the same mode of transport
    // if (curr.mode !== next.mode) {
    //   continue; // e.g. airplane → car, bus → train — not a layover pair
    // }

    // --- 1) Layover window: arrival1 → departure2 (for duration text) ----
    const layoverStart = parseLocalDateTime(
      parsedArr1.yyyyMmDd,
      parsedArr1.shortTime
    );
    const layoverEnd = parseLocalDateTime(
      parsedDep2.yyyyMmDd,
      parsedDep2.shortTime
    );
    const layoverMins = Math.max(
      0,
      Math.round((layoverEnd - layoverStart) / 60000)
    );

    // Only consider layovers between 30 mins and 24h
    if (!(layoverMins > 30 && layoverMins < 24 * 60)) {
      continue;
    }

    // --- 2) Travel corridor window: departure1 → arrival2 -----------------
    // This is the window we use to *protect* against impossible inserts
    // (hotel checkins, car rentals, etc in the middle of travel).
    const travelStart =
      parsedDep1 && parsedDep1.yyyyMmDd && parsedDep1.shortTime
        ? parseLocalDateTime(parsedDep1.yyyyMmDd, parsedDep1.shortTime)
        : layoverStart; // fallback
    const travelEnd =
      parsedArr2 && parsedArr2.yyyyMmDd && parsedArr2.shortTime
        ? parseLocalDateTime(parsedArr2.yyyyMmDd, parsedArr2.shortTime)
        : layoverEnd; // fallback

    const travelStartMs = travelStart.getTime();
    const travelEndMs = travelEnd.getTime();

    // Build layover title
    const code =
      next.originCode ||
      extractAirportCode(next.originName) ||
      extractAirportCode(curr.destinationName) ||
      "";

    const city =
      extractCity(next.originName) ||
      extractCity(curr.destinationName) ||
      "Airport";

    const title = `Layover in ${city}${code ? ` (${code})` : ""}`;

    const layoverRow = {
      id: `${ensureId(curr.id)}_layover_${ensureId(next.id)}`,
      type: "transportation",
      mode: "layover",
      title,
      durationMinutes: layoverMins,
      durationText: humanizeMinutes(layoverMins),
      _instance: "full",
      _day: curr._day, // show on the arrival day
    };

    layoverPairs.push({
      arrivalIndex: i,
      departureIndex: j,
      layoverRow,
      travelStartMs,
      travelEndMs,
      deferred: [],
    });
  }

  if (!layoverPairs.length) {
    return daySegments;
  }

  const layoverByArrival = new Map();
  const layoverByDeparture = new Map();
  for (const lp of layoverPairs) {
    layoverByArrival.set(lp.arrivalIndex, lp);
    layoverByDeparture.set(lp.departureIndex, lp);
  }

  // Track segments that we will re-insert later (deferred)
  const used = new Array(n).fill(false);

  // Pass 1: find non-core-transport segments that fall inside any "travel corridor"
  for (let idx = 0; idx < n; idx++) {
    const seg = daySegments[idx];

    // Skip layovers themselves
    if (seg.type === "transportation" && seg.mode === "layover") continue;

    // Skip the core transport legs that define layovers
    if (isCoreTransport(seg)) continue;

    const segMs = segmentTimeMs(seg);
    if (!segMs) continue;

    for (const lp of layoverPairs) {
      // Only segments whose representative time is inside the *travel*
      // window are candidates for deferral.
      if (segMs >= lp.travelStartMs && segMs <= lp.travelEndMs) {
        // Activities are allowed inside layovers/travel windows.
        if (seg.type === "activity") {
          // keep where it is
        } else {
          // Defer accommodation, rentals, etc.
          lp.deferred.push(idx);
          used[idx] = true;
        }
        break;
      }
    }
  }

  // Pass 2: Build final ordered list:
  //  - keep non-deferred segments in original order
  //  - after each "arrivalIndex" insert layover row
  //  - after each "departureIndex" insert deferred segments
  const out = [];

  for (let idx = 0; idx < n; idx++) {
    if (used[idx]) {
      continue; // skip deferred for now
    }

    const seg = daySegments[idx];
    out.push(seg);

    // After the *arrival* leg, insert the layover
    const lpStart = layoverByArrival.get(idx);
    if (lpStart) {
      out.push(lpStart.layoverRow);
    }

    // After the *second* transport, re-insert deferred segments
    const lpEnd = layoverByDeparture.get(idx);
    if (lpEnd && lpEnd.deferred.length) {
      // Sort by original index to keep stable order
      lpEnd.deferred.sort((a, b) => a - b);
      for (const dIdx of lpEnd.deferred) {
        out.push(daySegments[dIdx]);
      }
    }
  }

  return out;
}

// previous
// function insertLayovers(daySegments) {
//   if (!Array.isArray(daySegments) || daySegments.length < 2) return daySegments;

//   const n = daySegments.length;

//   // 1) Identify indices of transportation segments (non-layover)
//   const transportIndexes = [];
//   for (let i = 0; i < n; i++) {
//     if (isTransportation(daySegments[i])) {
//       transportIndexes.push(i);
//     }
//   }
//   if (transportIndexes.length < 2) return daySegments;

//   // 2) Build layover/stopover pairs
//   const layoverPairs = [];
//   const MIN_LAYOVER_MINUTES = 30;
//   const MAX_LAYOVER_MINUTES = 24 * 60;

//   for (let t = 0; t < transportIndexes.length - 1; t++) {
//     const i = transportIndexes[t];
//     const j = transportIndexes[t + 1];

//     const curr = daySegments[i];
//     const next = daySegments[j];

//     const currSpan = getSegmentLocalSpan(curr);
//     const nextSpan = getSegmentLocalSpan(next);

//     const arrMs = currSpan.endMs; // arrival
//     const depMs = nextSpan.startMs; // next departure

//     if (arrMs == null || depMs == null || depMs <= arrMs) continue;

//     const mins = Math.max(0, Math.round((depMs - arrMs) / 60000));
//     if (mins < MIN_LAYOVER_MINUTES) continue;

//     // Layout: require "same airport" or equivalent
//     const sameAirport =
//       (curr.destinationCode &&
//         next.originCode &&
//         curr.destinationCode === next.originCode) ||
//       (!!curr.destination &&
//         !!next.origin &&
//         normalize(curr.destination) === normalize(next.origin)) ||
//       (!!curr.destinationName &&
//         !!next.originName &&
//         normalize(curr.destinationName) === normalize(next.originName));

//     if (!sameAirport) continue;

//     const code =
//       next.originCode ||
//       extractAirportCode(next.originName) ||
//       extractAirportCode(curr.destinationName) ||
//       "";
//     const city =
//       extractCity(next.originName) ||
//       extractCity(curr.destinationName) ||
//       "Airport";

//     const isStopover = mins >= MAX_LAYOVER_MINUTES;
//     const label = isStopover ? "Stopover" : "Layover";
//     const title = `${label} in ${city}${code ? ` (${code})` : ""}`;

//     const layoverRow = {
//       id: `${ensureId(curr.id)}_layover_${ensureId(next.id)}`,
//       type: "transportation",
//       mode: "layover",
//       title,
//       durationMinutes: mins,
//       durationText: humanizeMinutes(mins),
//       _instance: "full",
//       _day: curr._day,
//       _sortMinutes: 720, // mid-day, timeKeyForSort uses this
//     };

//     layoverPairs.push({
//       arrivalIndex: i,
//       departureIndex: j,
//       startMs: arrMs,
//       endMs: depMs,
//       layoverRow,
//       moveBefore: [], // indices of segments to show before curr
//       moveAfter: [], // indices of segments to show after next
//     });
//   }

//   if (!layoverPairs.length) return daySegments;

//   // Pre-index pairs by arrival/departure index
//   const pairByArrival = new Map();
//   const pairByDeparture = new Map();
//   for (const lp of layoverPairs) {
//     pairByArrival.set(lp.arrivalIndex, lp);
//     pairByDeparture.set(lp.departureIndex, lp);
//   }

//   // 3) Classify segments that fall INSIDE layover windows
//   const used = new Array(n).fill(false); // mark segments we relocate

//   for (let idx = 0; idx < n; idx++) {
//     const seg = daySegments[idx];
//     if (!seg) continue;
//     if (isTransportation(seg) || isLayover(seg)) continue;

//     const span = getSegmentLocalSpan(seg);
//     const startMs = span.startMs;
//     const endMs = span.endMs;

//     if (startMs == null && endMs == null) continue;

//     for (const lp of layoverPairs) {
//       const insideStart =
//         startMs != null && startMs >= lp.startMs && startMs <= lp.endMs;
//       const insideEnd =
//         endMs != null && endMs >= lp.startMs && endMs <= lp.endMs;

//       // Activities are allowed to stay inside layover windows.
//       if (isActivity(seg) && (insideStart || insideEnd)) {
//         // no move -> remain between flights
//         continue;
//       }

//       const isRuleSegment =
//         isAccommodation(seg) ||
//         ((seg.terms || "").toLowerCase() === "rent" &&
//           seg.type === "transportation");

//       if (!isRuleSegment) {
//         continue;
//       }

//       // EXCEPTION: if BOTH start and end are inside, keep it as-is.
//       if (insideStart && insideEnd) {
//         continue;
//       }

//       // "Display after" rule:
//       //   start inside window, end outside -> move AFTER second transport.
//       if (insideStart && !insideEnd) {
//         lp.moveAfter.push(idx);
//         used[idx] = true;
//         break;
//       }

//       // "Display before" rule:
//       //   end inside window, start outside -> move BEFORE first transport.
//       if (!insideStart && insideEnd) {
//         lp.moveBefore.push(idx);
//         used[idx] = true;
//         break;
//       }
//     }
//   }

//   // Keep the original order among moved indices
//   for (const lp of layoverPairs) {
//     lp.moveBefore.sort((a, b) => a - b);
//     lp.moveAfter.sort((a, b) => a - b);
//   }

//   // 4) Build final ordered list for the day
//   const out = [];
//   for (let idx = 0; idx < n; idx++) {
//     // If there are "moveBefore" segments tied to this transport, emit them first.
//     const beforePair = pairByArrival.get(idx);
//     if (beforePair && beforePair.moveBefore.length) {
//       for (const dIdx of beforePair.moveBefore) {
//         out.push(daySegments[dIdx]);
//       }
//     }

//     // Emit current segment if it was not consumed by reordering
//     if (!used[idx]) {
//       out.push(daySegments[idx]);
//     }

//     // If this is an arrivalIndex, emit the layover/stopover row right after it.
//     if (beforePair) {
//       out.push(beforePair.layoverRow);
//     }

//     // If this is the departureIndex, emit any "moveAfter" segments after the second transport.
//     const afterPair = pairByDeparture.get(idx);
//     if (afterPair && afterPair.moveAfter.length) {
//       for (const dIdx of afterPair.moveAfter) {
//         out.push(daySegments[dIdx]);
//       }
//     }
//   }

//   return out;
// }

/**
 * Debug helper: log a day's segments in the order they will render.
 *
 * Usage:
 *   const days = groupByDay(segments);
 *   debugTimelineForDay(days[0]);
 *
 * Or:
 *   debugTimelineForDay("2025-06-07", days.find(d => d.day === "2025-06-07").segments);
 *
 * This prints to console:
 *   - index in day
 *   - id, type, mode, _instance, _day
 *   - sortTimeKey (minutes since midnight)
 *   - local start/end times used for layover logic
 */
export function debugTimelineForDay(label, day, segments) {
  console.debug(
    `\n--- ${label} timeline for ${day} --- (count: ${segments.length})`
  );

  segments.forEach((seg, index) => {
    // Use normalizeSpan to get start/end yyyy-mm-dd + hh:mm (local-ish)
    const span = normalizeSpan(seg) || {};
    const startDate = span.startDate || "";
    const startTime = span.startTime || "";
    const endDate = span.endDate || "";
    const endTime = span.endTime || "";

    const sortKey =
      typeof seg.sortTimeKey === "number"
        ? seg.sortTimeKey
        : timeKeyForSort(seg);

    console.debug(
      [
        `${index.toString().padStart(2, "0")}.`,
        `id=${seg.id || "∅"}`,
        `type=${seg.type || "∅"}`,
        `mode=${seg.mode || "∅"}`,
        `terms=${seg.terms || "∅"}`,
        `_instance=${seg._instance || "full"}`,
        `day=${seg._day || "∅"}`,
        `start=${startDate} ${startTime}`,
        `end=${endDate} ${endTime}`,
        `sortKey=${sortKey}`,
      ].join(" | ")
    );
  });

  console.debug(`--- end ${label} ${day} ---\n`);
}
// previous
// export function debugTimelineForDay(dayOrObj, maybeSegments) {
//   let day;
//   let segments;

//   // Support both:
//   //   debugTimelineForDay({ day, segments })
//   //   debugTimelineForDay("YYYY-MM-DD", segments)
//   if (typeof dayOrObj === "string") {
//     day = dayOrObj;
//     segments = Array.isArray(maybeSegments) ? maybeSegments : [];
//   } else if (dayOrObj && typeof dayOrObj === "object") {
//     day = dayOrObj.day;
//     segments = Array.isArray(dayOrObj.segments) ? dayOrObj.segments : [];
//   } else {
//     console.warn(
//       "[debugTimelineForDay] Invalid arguments",
//       dayOrObj,
//       maybeSegments
//     );
//     return;
//   }

//   if (!day) {
//     console.warn("[debugTimelineForDay] Missing day");
//     return;
//   }

//   if (!segments.length) {
//     console.info(`[debugTimelineForDay] No segments for day ${day}`);
//     return;
//   }

//   console.group(`Itinerary debug for day ${day}`);

//   segments.forEach((seg, index) => {
//     const span = getSegmentLocalSpan(seg); // local start/end in ms
//     const key = timeKeyForSort(seg); // minutes since (local) midnight

//     // Convert ms -> readable local datetime (best-effort, dev-only)
//     const startLocal = span.startMs != null ? new Date(span.startMs) : null;
//     const endLocal = span.endMs != null ? new Date(span.endMs) : null;

//     const startLocalStr = startLocal ? startLocal.toISOString() : "(none)";
//     const endLocalStr = endLocal ? endLocal.toISOString() : "(none)";

//     console.log({
//       index,
//       id: seg.id,
//       type: seg.type,
//       mode: seg.mode,
//       terms: seg.terms,
//       instance: seg._instance,
//       day: seg._day,
//       sortTimeKey: key,
//       startLocalMs: span.startMs,
//       endLocalMs: span.endMs,
//       startLocalIso: startLocalStr,
//       endLocalIso: endLocalStr,
//       isTransportation: isTransportation(seg),
//       isAccommodation: isAccommodation(seg),
//       isActivity: isActivity(seg),
//       isLayover: isLayover(seg),
//     });
//   });

//   console.groupEnd();
// }

// -------------------------------------------------------------
// Internal helpers: misc utilities
// -------------------------------------------------------------

function visibleTime(t) {
  return t || "";
}

function toYMD(d) {
  return d.toISOString().slice(0, 10);
}

function ensureId(id) {
  return id ?? Math.random().toString(36).slice(2);
}

function normalize(str) {
  return (str || "").trim().toLowerCase();
}

function extractAirportCode(name) {
  if (!name) return "";
  // Look for patterns like "DEN", " (DEN)", "DEN)" etc.
  const m = /([A-Z]{3})\)?$/.exec(name.trim());
  return m ? m[1] : "";
}

function extractCity(name) {
  if (!name) return "";
  // Handle "Denver, CO (DEN)" -> "Denver, CO"
  const withoutCode = name.replace(/\([A-Z]{3}\)\s*$/, "").trim();
  return withoutCode || name;
}

function humanizeMinutes(mins) {
  const m = Math.max(0, Math.round(mins));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (!h) return `${mm}m`;
  if (!mm) return `${h}h`;
  return `${h}h ${mm}m`;
}
